import ApiResourceManager from '../ApiResourceManager';

/**
 * Partner payment rails + Spreedly gateway lifecycle, consumed by the SuperAdmin
 * partner drawer. Every method takes an explicit serviceProviderId and overrides
 * the on-behalf header, so the drawer can act on any partner regardless of the
 * provider currently selected in the sidebar (same pattern as
 * ServiceProviderSettingsManager).
 *
 * The company-scoped calls additionally pin the company header — and still pass
 * the partner header, because the ambient sidebar header would otherwise leak in
 * (usually the master provider) and fail the backend's provider assertions.
 */
export default class SpreedlyManager extends ApiResourceManager {
  /**
   * The partner's own read-only enablement (self-service page): whether FA
   * switched the rail on, provisioned the environment, and the publishable key.
   * Deliberately no id override — the acting provider comes from the tenant
   * middleware.
   */
  getPartnerPaymentGateway = () =>
    this.request({
      method: 'GET',
      url: '/partner-settings/payment-gateway',
    });

  getPaymentRails = (serviceProviderId = undefined) =>
    this.request({
      method: 'GET',
      url: '/service-provider-payment-rails',
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  updatePaymentRails = (data, serviceProviderId = undefined) =>
    this.request({
      method: 'PUT',
      url: '/service-provider-payment-rails',
      data,
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  getProcessingFeeRates = (serviceProviderId = undefined) =>
    this.request({
      method: 'GET',
      url: '/service-provider-processing-fee-rates',
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  updateProcessingFeeRates = (data, serviceProviderId = undefined) =>
    this.request({
      method: 'PUT',
      url: '/service-provider-processing-fee-rates',
      data,
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  getGateways = (serviceProviderId = undefined, params = undefined) =>
    this.request({
      method: 'GET',
      url: '/spreedly-gateways',
      ...(params && { params }),
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  getGatewayOptions = (serviceProviderId = undefined, params = undefined) =>
    this.request({
      method: 'GET',
      url: '/spreedly-gateway-options',
      ...(params && { params }),
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  createGateway = (data, serviceProviderId = undefined) =>
    this.request({
      method: 'POST',
      url: '/spreedly-gateways',
      data,
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  setDefaultGateway = (gatewayId, serviceProviderId = undefined) =>
    this.request({
      method: 'PUT',
      url: `/spreedly-gateways/${gatewayId}/default`,
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  /**
   * Without `force` the backend refuses (422 `spreedly_gateway_still_assigned`)
   * while restaurants are still assigned — the caller re-confirms and retries
   * with `force: true`.
   *
   * `force` rides in the query string, not a DELETE body: proxies are free to
   * drop a body off a DELETE. Laravel's `boolean('force')` reads the query bag
   * just the same.
   */
  redactGateway = (gatewayId, serviceProviderId = undefined, force = false) =>
    this.request({
      method: 'DELETE',
      url: `/spreedly-gateways/${gatewayId}`,
      ...(force && { params: { force: 1 } }),
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  provisionScaProvider = (gatewayId, data, serviceProviderId = undefined) =>
    this.request({
      method: 'POST',
      url: `/spreedly-gateways/${gatewayId}/sca-provider`,
      data,
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  runGatewayCheck = (gatewayId, data, serviceProviderId = undefined) =>
    this.request({
      method: 'POST',
      url: `/spreedly-gateways/${gatewayId}/checks`,
      data,
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  getCompaniesPaymentGateways = (params, serviceProviderId = undefined) =>
    this.request({
      method: 'GET',
      url: '/companies-payment-gateways',
      params,
      ...(serviceProviderId && { headers: { 'x-on-behalf-of-service-provider': serviceProviderId } }),
    });

  assignCompanyGateway = (companyId, data, serviceProviderId = undefined) =>
    this.request({
      method: 'PUT',
      url: `/companies/${companyId}/spreedly-gateway`,
      data,
      headers: {
        'x-on-behalf-of-company': companyId,
        ...(serviceProviderId && { 'x-on-behalf-of-service-provider': serviceProviderId }),
      },
    });

  switchCompanyPaymentGateway = (companyId, data, serviceProviderId = undefined) =>
    this.request({
      method: 'PUT',
      url: `/companies/${companyId}/payment-gateway`,
      data,
      headers: {
        'x-on-behalf-of-company': companyId,
        ...(serviceProviderId && { 'x-on-behalf-of-service-provider': serviceProviderId }),
      },
    });
}
